import { Action } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import ReactDOM from 'react-dom';
import React from 'react';
import * as R from 'ramda';

import { PrinterScript, formatReceipt } from 'Systems/PrinterScript/exports';
import {
  getCurrentPrinterIntegration,
  getSupportedPrinterIntegrations,
} from 'reducers/configs/settings';
import {
  PrinterMetadata,
  printPrinterTest,
} from 'Systems/PrinterScript/core/PrinterScript';
import { sleep } from 'utils';
import { PatchscriptRender } from 'containers/Forms/Settings/views/Debug/components/ReceiptSchema/Editor/printer/sampleReactPrinter';
import { print } from 'utils/print';
import {
  PatchScript,
  RItem,
} from 'containers/Forms/Settings/views/Debug/components/ReceiptSchema/Editor/schema/comms';
import { getCafaEntry } from 'reducers/cafaConfigs';
import { INTEGRATION_TYPES } from 'constants/CAFA';
import { getClientCode, getSessionKey } from 'reducers/Login';
import * as printingMS from 'services/InstallerApp/printingMS';
import { RootState } from 'reducers';

import {
  sendOpenCashDrawerToWrapper,
  sendPatchscriptToWrapper,
  sendReceiptDataToWrapper,
  sendZReportDataToWrapper,
} from './wrapper';
import { getIsMSRunning } from './utils';

let browserQueue = Promise.resolve('');
export const renderElement = (
  element: JSX.Element,
  width: number,
  scale: number,
) => {
  browserQueue = Promise.all([browserQueue, import('dom-to-image')]).then(
    async ([, { default: domToImage }]) => {
      const div = document.getElementById('printPreview');
      ReactDOM.render(
        <div
          style={{
            display: 'inline-block',
            width: width / scale,
            position: 'fixed',
            zIndex: -1000,
            top: 0,
            left: 0,
          }}
        >
          {element}
        </div>,
        div,
      );

      await sleep(0.3);
      div?.style.setProperty('display', 'block');
      const el = div?.children[0];
      const blob = (
        await domToImage.toPng(el, {
          width: (el?.clientWidth ?? 1) * scale,
          height: (el?.clientHeight ?? 1) * scale,
          style: {
            transform: `scale(${scale})`,
            'transform-origin': 'top left',
          },
        })
      ).split(',')[1];
      div?.style.removeProperty('display');
      return blob;
    },
  );
  return browserQueue;
};

/**
 * Renders Actual Reports receipt html to DOM (scaled to the size of given width) and converts it to an image.
 * If receipt consists of multiple pages combines them all into one long page
 * @returns image blob
 */
export const renderARHtmlForPrintMS = (
  html: string,
  width: number,
  scaleMultiplier = null as number | null,
): Promise<string> => {
  browserQueue = Promise.all([browserQueue, import('dom-to-image')]).then(
    async ([, { default: domToImage }]) => {
      const scale = scaleMultiplier ?? 1;
      const div = document.getElementById('printPreview');
      if (!div) {
        return '';
      }
      ReactDOM.render(
        <div
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: html }}
          style={{
            display: 'inline-block',
            position: 'fixed',
            zIndex: -1000,
            width: width / scale,
            top: 0,
            left: 0,
          }}
        />,
        div,
      );

      await sleep(0.3);
      div.style.setProperty('display', 'block');
      const pages = div.querySelectorAll<HTMLDivElement>('[data-page]');
      let [elementToPrint] = pages;

      // Combine multiple pages into one long page
      if (pages.length > 1) {
        const pageContainer = document.createElement('div');
        div.firstChild?.insertBefore(pageContainer, pages[0]);
        pageContainer.style.width = pages[0].style.width;

        pages.forEach((page, idx) => {
          pageContainer.appendChild(page);

          const height =
            idx < pages.length - 1
              ? page.clientHeight
              : // Last page height is calculated based on its content height + hardcoded bottom padding
                // Otherwise last page might be mostly empty (and I care about the trees)
                R.pipe(
                  Array.from,
                  R.map(
                    child => parseFloat(child.style.top) + child.offsetHeight,
                  ),
                  R.sort(R.descend(R.identity)),
                  R.head,
                  R.add(65),
                )(page.children);

          pageContainer.style.height = `${pageContainer.clientHeight +
            height}px`;
        });
        elementToPrint = pageContainer;
      }

      const autoScale = !scaleMultiplier
        ? width / elementToPrint.clientWidth
        : scale;

      const blob = (
        await domToImage.toPng(elementToPrint, {
          width,
          height: elementToPrint.clientHeight * autoScale,
          style: {
            transform: `scale(${autoScale})`,
            'transform-origin': 'top left',
          },
        })
      ).split(',')[1];

      div.style.removeProperty('display');
      ReactDOM.unmountComponentAtNode(div);

      return blob;
    },
  );
  return browserQueue;
};

const testPrinterType = (printer: PrinterMetadata) => async () => {
  const script = printPrinterTest(new PrinterScript(printer)).toString();
  await printingMS.printReceiptWs(script);
};

export const MSActions = {
  openCashDrawer: () => async (
    dispatch: ThunkDispatch<RootState, unknown, Action>,
    getState: () => RootState,
  ) => {
    const useLegacy = localStorage.getItem('MS.print.legacy');
    const metaData = getCurrentPrinterIntegration(getState());

    if (useLegacy) {
      return dispatch(
        MSActions.print(new PrinterScript(metaData).toOpenCashDrawer()),
      );
    }
    return printingMS.openCashDrawer();
  },
  print: (script: string) => async () => {
    return printingMS.printReceiptWs(script);
  },
  printReceiptBySaleId: (salesDocumentId: number) => async (
    dispatch: ThunkDispatch<RootState, unknown, Action>,
    getState: () => RootState,
  ) => {
    const receiptPrintoutApiIsRunning = await dispatch(
      getIsMSRunning('receipt-printout-api'),
    );
    const { id } =
      getCafaEntry(
        'salesReceipt',
        INTEGRATION_TYPES.patchScriptTemplate,
      )(getState()) ?? {};

    if (!id) {
      throw new Error('Unable to find patchscript template id');
    }
    if (!receiptPrintoutApiIsRunning) {
      throw new Error('Receipt printout API MS not running');
    }

    const templateId = String(id);

    const clientCode = getClientCode(getState());
    const sessionKey = getSessionKey(getState());

    const useWebSocket = localStorage.getItem('MS.print.bySaleId.ws');

    if (useWebSocket) {
      const data = {
        salesDocumentId,
        templateId,
        printoutType: 'PATCH_SCRIPT',
        clientCode,
        sessionKey,
      };
      const payload = JSON.stringify(data);
      return printingMS.printReceiptWs(payload);
    }

    return printingMS.printReceiptHttp(
      salesDocumentId,
      templateId,
      clientCode,
      sessionKey,
    );
  },
  testAll: () => async (
    dispatch: ThunkDispatch<RootState, unknown, Action>,
    getState: () => RootState,
  ) => {
    Object.values(getSupportedPrinterIntegrations(getState())).reduce(
      async (prevPromise, int) => {
        await prevPromise;
        await sleep(1);
        dispatch(testPrinterType(int));
      },
      Promise.resolve(),
    );
  },
};

export const WrapperActions = {
  openCashDrawer: sendOpenCashDrawerToWrapper,
  printReceipt: ({ data, link }, options = {}) =>
    sendReceiptDataToWrapper({ receiptLink: link, data }, options),
  printZReport: ({ data, link, html }) =>
    sendZReportDataToWrapper({ data, link, html }),
  printCoupon: () => {
    throw new Error('Wrapper: coupon printing not implemented yet.');
  },
  printPatchscript: (data: PatchScript) => sendPatchscriptToWrapper(data),
};

export const BrowserActions = {
  print: ({
    url,
    html,
    fullHtml,
  }: {
    url?: string;
    html?: string;
    fullHtml?: string;
  }) => async () => {
    // TODO: This could also be changed to do inline printing instead of a popup
    // And then it would also need to use the queue
    return print({ url, html, fullHtml });
  },
  printPatchScript: (patchScript: RItem[]) => async () => {
    browserQueue = browserQueue.then(
      () =>
        new Promise(resolve => {
          ReactDOM.render(
            <PatchscriptRender receiptData={patchScript} />,
            document.getElementById('printPreview'),
          );
          setTimeout(() => {
            window.print();
            resolve(patchScript.toString());
          }, 300);
        }),
    );
    return browserQueue;
  },
};

export const renderPatchscript = (
  patchScript: RItem[],
  width: number,
  scale: number,
) => {
  const element = <PatchscriptRender receiptData={patchScript} />;
  return renderElement(element, width, scale);
};

export const wrapPngData = (data: string) =>
  [{ type: 'image', blob: data }] as RItem[];
